import React, { ChangeEvent, useEffect, useState } from 'react';
import { Input, cn } from '@chapche/ui';

const CAPS_LOCK_KEY = 'CapsLock';
const SAFARI_BROWSER = 'Safari';
const YANDEX_BROWSER = 'YaBrowser';

const checkIsYandexOrSafariBrowser = (): boolean =>
    window.navigator.userAgent.includes(YANDEX_BROWSER) ||
    window.navigator.userAgent.includes(SAFARI_BROWSER);

interface PasswordInputProps {
    label: string;
    password: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    showLabel?: string;
    hideLabel?: string;
    hasError?: boolean;
    capsLockLabel?: string;
}

/**
 * Reusable password input for login forms.
 * Handles show/hide toggle and Caps Lock warning.
 */
const PasswordInput = ({
    label,
    password,
    onChange,
    showLabel = 'Показать',
    hideLabel = 'Скрыть',
    hasError = false,
    capsLockLabel = 'Caps Lock включён',
}: PasswordInputProps) => {
    const [passwordIsShown, setPasswordIsShown] = useState(false);
    const [capslockIsOn, setCapslockIsOn] = useState(false);

    const handleKeyUp = (e: KeyboardEvent) => {
        if (e.getModifierState(CAPS_LOCK_KEY) && e.key === CAPS_LOCK_KEY) {
            setCapslockIsOn((prev) => !prev);
        } else if (e.key === CAPS_LOCK_KEY) {
            setCapslockIsOn(false);
        }
    };

    useEffect(() => {
        document.addEventListener('keyup', handleKeyUp);
        return () => document.removeEventListener('keyup', handleKeyUp);
    }, []);

    return (
        <>
            <label className="block__text password__label relative flex flex-col gap-8" autoComplete="off">
                {label}
                {checkIsYandexOrSafariBrowser() && (
                    <input type="password" autoComplete="off" style={{ display: 'none' }} />
                )}
                <Input
                    fullWidth
                    className={cn('mt-0', hasError && 'password__input_error')}
                    type={!passwordIsShown && password ? 'password' : 'text'}
                    value={password}
                    onChange={onChange}
                    autoComplete="off"
                    state={hasError ? 'error' : 'default'}
                />
                {password && (
                    <span
                        className="block__bullet password__text cursor-pointer"
                        onClick={() => setPasswordIsShown((v) => !v)}
                    >
                        {passwordIsShown ? hideLabel : showLabel}
                    </span>
                )}
            </label>
            {capslockIsOn && (
                <p className="block__bullet password__warning">{capsLockLabel}</p>
            )}
        </>
    );
};

export default PasswordInput;
